<template>
  <div class="h-full">
    <div v-if="!fetching" class="h-full flex flex-col">
      <div class="w-full pr-3 flex-grow pl-2 pb-2 max-w-1000">
        <ValidationObserver v-slot="{ errors }" ref="validationObserver">
          <FormInput v-model="name" rules="required" class="w-300 mt-3" :label="$t('projects.name')" name="projectName" :error="errors.projectName" />

          <FormInput v-model="brandName" rules="required" class="w-300 mt-3" :label="$t('projects.brand_name')" name="brandName" :error="errors.brandName" />

          <div class="flex flex-row mt-3">
            <FormSelectMultiBadge v-model="from" label="Auth Emаil Sender" class="w-300 mt-1" :options="emailFromList" searchable :clearable="false" />
            <div class="flex flex-row items-center mt-1 ml-3">
              <FormInput v-if="from?.badge1 === 'domain'" v-model="fromEmail" rules="required" :error="errors.from_em" name="from_em" label="From Emаil" class="w-50" />
              <div class="w-100 text-13 ml-1" :class="{ 'mt-4': !errors.from_em }" v-if="from?.badge1 === 'domain'">@{{ from?.value }}</div>
            </div>
          </div>

          <div class="mt-3 w-300">
            <FormInput
              v-model="workflowStepTimeout"
              type="number"
              :min="stepTimeoutBounds.MIN_MINUTES"
              :max="stepTimeoutBounds.MAX_MINUTES"
              :placeholder="String(stepTimeoutBounds.DEFAULT_MINUTES)"
              label="Workflow step timeout (minutes)"
              name="workflowStepTimeout"
              :error="workflowStepTimeoutError"
              @update:modelValue="workflowStepTimeoutError = ''"
            />
            <div class="text-12 text-semi-dark-text mt-1">
              How long each workflow step, such as one data export, may run before it is stopped. Leave empty for {{ stepTimeoutBounds.DEFAULT_MINUTES }} minutes.
            </div>
          </div>

          <div class="mt-3">
            <FormLabel label="Theme"></FormLabel>

            <div class="flex mt-0.5">
              <div
                v-for="(t, index) in themes"
                :key="index"
                class="mr-2 w-8 h-8 border-2 border-solid rounded-full p-0.5 cursor-pointer"
                :class="{ 'border-primary': theme === t, 'border-transparent': theme !== t }"
                @click="changeTheme(t)"
              >
                <div class="rounded-full overflow-hidden border-[1px] w-full h-full border-gray-300">
                  <div
                    class="circle"
                    :style="{ 'border-top-color': themeConfigs[t][0], 'border-left-color': themeConfigs[t][0], 'border-right-color': themeConfigs[t][1], 'border-bottom-color': themeConfigs[t][1] }"
                    v-tooltip="t"
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-4">
            <FormLabel label="Logo's"></FormLabel>
          </div>
          <div class="mt-2 flex max-md:flex-wrap">
            <div class="w-full md:w-1/2 flex">
              <div class="w-200 h-20 mr-3 border-dashed border-stroke border rounded-8 flex items-center justify-center">
                <img class="max-w-[200px] max-h-[50px]" :src="logos.PROJECT" />
              </div>
              <div>
                <div class="font-500 text-12 mt-0.5">Project Logo</div>
                <div class="text-12 text-gray-500">Recommended size is 128x32px</div>
                <input type="file" name="project" ref="projectInput" class="hidden" accept="image/png" @change="onFileInputChange($event, IMAGE_TYPES.PROJECT)" />
                <PillButton outlined icon="upload" text="Upload" class="mt-2" @click="openFile(IMAGE_TYPES.PROJECT)" />
              </div>
            </div>
            <div class="w-full md:w-1/2 flex">
              <div class="flex">
                <div class="w-200 h-20 mr-3 border-dashed border-stroke border rounded-8 flex items-center justify-center bg-[rgb(var(--color-menu-background))]">
                  <img class="max-w-[50px] max-h-[50px]" :src="logos.NAVIGATION" />
                </div>
                <div>
                  <div class="font-500 text-12 mt-0.5">Navigation Logo</div>
                  <div class="text-12 text-gray-500">Recommended size is 36x36px</div>
                  <input type="file" name="navigation" ref="navigationInput" class="hidden" accept="image/png" @change="onFileInputChange($event, IMAGE_TYPES.NAVIGATION)" />
                  <PillButton outlined icon="upload" text="Upload" class="mt-2" @click="openFile(IMAGE_TYPES.NAVIGATION)" />
                </div>
              </div>
            </div>
          </div>
          <div class="mt-4 flex max-md:flex-wrap">
            <div class="w-full md:w-1/2 flex">
              <div class="w-200 h-20 mr-3 border-dashed border-stroke border rounded-8 flex items-center justify-center">
                <img class="max-w-[50px] max-h-[50px]" :src="logos.FAVICON" />
              </div>
              <div>
                <div class="font-500 text-12 mt-0.5">Favicon</div>
                <div class="text-12 text-gray-500">Recommended size is 16x16px</div>
                <input type="file" name="favicon" ref="faviconInput" class="hidden" accept="image/png" @change="onFileInputChange($event, IMAGE_TYPES.FAVICON)" />
                <PillButton outlined icon="upload" text="Upload" class="mt-2" @click="openFile(IMAGE_TYPES.FAVICON)" />
              </div>
            </div>
            <div class="w-full md:w-1/2 flex">
              <div class="w-200 h-20 mr-3 border-dashed border-stroke border rounded-8 flex items-center justify-center">
                <img class="rounded-full max-w-[50px] max-h-[50px]" :src="logos.CONVERSATION" />
              </div>
              <div>
                <div class="font-500 text-12 mt-0.5">Conversation Icon</div>
                <div class="text-12 text-gray-500">Recommended size is 36x36px</div>
                <input type="file" name="bubble_icon" ref="bubbleIconInput" class="hidden" accept="image/png,image/jpg,image/jpeg" @change="onFileInputChange($event, IMAGE_TYPES.CONVERSATION)" />
                <PillButton outlined icon="upload" text="Upload" class="mt-2" @click="openFile(IMAGE_TYPES.CONVERSATION)" />
              </div>
            </div>
          </div>

          <div>
            <PillButton class="mt-8" :text="$t('projects.update_project')" primary @click="onSaveClick" :disabled="loading" :loading="loading"></PillButton>
          </div>

          <div class="mt-10">
            <div class="text-18 font-500 mb-2">{{ $t('projects.danger_zone') }}</div>
            <div class="border border-solid border-alert border-opacity-50 rounded-8">
              <div class="flex justify-between items-center p-4">
                <div>
                  <div class="font-600">{{ isDisabled ? $t('projects.enable_this') : $t('projects.disable_this') }}</div>
                  <div>{{ isDisabled ? $t('projects.enable_warning') : $t('projects.disable_warning') }}</div>
                </div>
                <PillButton :text="isDisabled ? $t('projects.enable_this') : $t('projects.disable_this')" :error="!isDisabled" :primary="isDisabled" @click="onDisableChange"></PillButton>
              </div>
              <Divider dense />
              <div class="flex justify-between items-center p-4">
                <div>
                  <div class="font-600">{{ $t('projects.delete_this') }}</div>
                  <div>{{ $t('projects.delete_warning') }}</div>
                </div>
                <PillButton :text="$t('projects.delete_this')" error @click="openDeleteProjectModal"></PillButton>
              </div>
            </div>
          </div>
        </ValidationObserver>
      </div>
    </div>
    <Modal v-if="fetching">
      <div class="flex flex-col font-700 items-center justify-center px-20 my-10">
        <div>{{ $t('loading_data') }}</div>
        <Icon name="loading_dots" class="w-14 mt-3 text-primary" />
      </div>
    </Modal>
  </div>
</template>
<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import DeleteModal from '../../../modals/DeleteModal.vue';
import { apiPutProjectLogo } from '../../../../helpers/api';
import { WORKFLOW_STEP_TIMEOUT } from '../../../../constants';

export default {
  name: 'Settings',
  data() {
    return {
      name: '',
      brandName: '',
      from: null,
      fromEmail: '',
      workflowStepTimeout: '',
      workflowStepTimeoutError: '',
      stepTimeoutBounds: WORKFLOW_STEP_TIMEOUT,
      themes: ['default', 'darkbluegray', 'darkblue', 'steelblue', 'orange', 'black', 'whitegreen'],
      themeConfigs: {
        default: ['#5B47C2', 'white'],
        darkbluegray: ['#243746', 'white'],
        darkblue: ['#061D60', 'white'],
        whitegreen: ['white', '#00c4b8'],
        steelblue: ['#5497C2', 'white'],
        orange: ['#E69B56', 'white'],
        black: ['#000000', 'white'],
      },
      loading: false,
      fetching: true,
      IMAGE_TYPES: {
        PROJECT: 'PROJECT',
        NAVIGATION: 'NAVIGATION',
        FAVICON: 'FAVICON',
        CONVERSATION: 'CONVERSATION',
      },
      logos: {},
    };
  },
  computed: {
    ...mapState(['projects', 'projectData', 'theme']),
    ...mapGetters(['getProjectEmailProviders']),
    navigationLogo() {
      if (this.theme.startsWith('white')) {
        return '/img/clients/fidsy/fidsy_bubble_icon.svg';
      }
      return '/img/clients/fidsy/fidsy_logo_white.svg';
    },
    project() {
      return this.projects[this.$route.params.projectId];
    },
    projectId() {
      return this.$route.params.projectId;
    },
    isDisabled() {
      return this.project.disabled;
    },
    emailFromList() {
      const list = Object.values(this.getProjectEmailProviders()).reduce((acc, item) => {
        const accounts = item?.config?.accounts || [];
        return [
          ...acc,
          ...accounts.map((account) => ({
            label: account.email || account.domain,
            value: account.email || account.domain,
            provider: item.provider,
            badge1: account.email ? 'email' : 'domain',
            badge2: item.provider,
          })),
        ];
      }, []);
      return list;
    },
  },
  beforeUnmount() {
    this.changeTheme(this.project?.theme || 'default');
  },
  methods: {
    ...mapActions(['fetchProject', 'changeTheme', 'fetchProjectEmailProviders', 'updateProject', 'deleteProject', 'showToastMessage', 'updateProjectDisabled']),
    openFile(type) {
      if (type === this.IMAGE_TYPES.CONVERSATION) {
        this.$refs.bubbleIconInput.click();
      } else if (type === this.IMAGE_TYPES.FAVICON) {
        this.$refs.faviconInput.click();
      } else if (type === this.IMAGE_TYPES.NAVIGATION) {
        this.$refs.navigationInput.click();
      } else {
        this.$refs.projectInput.click();
      }
    },
    onFileInputChange(evt, type) {
      const file = evt.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async (e) => {
        const arrayBuffer = e.target.result;
        const response = await apiPutProjectLogo({ project_id: this.projectId, file_type: type, type: file.type, file_name: file.name, file: arrayBuffer });
        if (response.status === 200) {
          this.logos[type] = response.data.url;
        } else {
          this.showToastMessage({ message: this.$t('projects.error_updating_logo'), type: 'error' });
        }
      };
      reader.readAsArrayBuffer(file);
    },
    async onSaveClick() {
      this.loading = true;
      const validationObserver = this.$refs?.validationObserver;
      const valid = await validationObserver.validate();

      const stepTimeout = this.readWorkflowStepTimeout();
      if (!valid.valid || stepTimeout.error) {
        this.workflowStepTimeoutError = stepTimeout.error || '';
        this.loading = false;
        return;
      }

      const { project_id, disabled } = this.project;
      await this.updateProject({
        name: this.name.trim(),
        brand_name: this.brandName.trim(),
        project_id,
        disabled,
        theme: this.theme,
        authEmail: this.from,
        authEmailFrom: this.fromEmail,
        workflow_step_timeout: stepTimeout.value,
      });
      this.loading = false;
    },
    /**
     * Reads the workflow step timeout field.
     * @returns {{value: (number|null), error?: string}} Whole minutes, null for the default, or a message to show on the field.
     */
    readWorkflowStepTimeout() {
      const raw = String(this.workflowStepTimeout ?? '').trim();
      if (!raw) return { value: null };
      const minutes = Number(raw);
      const { MIN_MINUTES, MAX_MINUTES } = WORKFLOW_STEP_TIMEOUT;
      if (!Number.isInteger(minutes) || minutes < MIN_MINUTES || minutes > MAX_MINUTES) {
        return { error: `Enter whole minutes from ${MIN_MINUTES} to ${MAX_MINUTES}, or leave empty.` };
      }
      return { value: minutes };
    },
    openDeleteProjectModal(project) {
      this.$showModal(DeleteModal, {
        subtitle: this.$t('projects.remove_project', { projectName: project.name }),
        info: null,
        onConfirm: () => this.onDeleteClicked(project),
      });
    },
    async onDeleteClicked() {
      this.loading = true;
      const res = await this.deleteProject(this.project.project_id);
      if (res) {
        this.$router.push('/project');
      }
      this.loading = false;
    },
    onDisableChange() {
      const subtitle = !this.project.disabled ? this.$t('projects.disable', { project: this.project.name }) : this.$t('projects.enable', { project: this.project.name });
      this.$showModal(DeleteModal, {
        subtitle,
        type: this.project.disabled ? 'primary' : 'error',
        onConfirm: async () => {
          await this.updateProjectDisabled({ project_id: this.projectId, disabled: !this.project.disabled });
        },
      });
    },
  },
  watch: {
    projectId: {
      async handler() {
        this.fetching = true;
        const response = await this.fetchProject({ projectId: this.projectId });
        await this.fetchProjectEmailProviders({ projectId: this.projectId });
        this.name = this.project?.name;
        this.brandName = response?.brand_name || response?.name || '';
        this.from = response?.authEmail;
        this.fromEmail = response?.authEmailFrom || '';
        this.workflowStepTimeout = response?.workflow_step_timeout ?? '';
        this.workflowStepTimeoutError = '';
        this.theme = response?.theme || this.theme;
        this.changeTheme(this.theme);
        this.logos.PROJECT = this.projectData?.[this.projectId]?.logos?.[this.IMAGE_TYPES.PROJECT] || '/img/clients/fidsy/fidsy_logo.svg';
        this.logos.NAVIGATION = this.projectData?.[this.projectId]?.logos?.[this.IMAGE_TYPES.NAVIGATION] || this.navigationLogo;
        this.logos.FAVICON = this.projectData?.[this.projectId]?.logos?.[this.IMAGE_TYPES.FAVICON] || '/img/icons/favicon-16x16.png';
        this.logos.CONVERSATION = this.projectData?.[this.projectId]?.logos?.[this.IMAGE_TYPES.CONVERSATION] || '/img/clients/fidsy/fidsy_bubble_icon.svg';
        this.fetching = false;
      },
      immediate: true,
    },
  },
};
</script>
<style scoped lang="scss">
.circle {
  border-width: 12px;
  border-style: solid;
  height: 0px;
  width: 0px;
}
</style>
